import { defineStore } from 'pinia';
import { uid } from 'quasar';
import { LocalStorage } from 'quasar';
import type { dynamicObj } from 'src/pages/assets';
import { useDocumentStore } from 'src/store/main';
import { documentLockApiService } from 'src/services';
import { releaseRequest } from 'src/types';
import { isUndefinedOrNull } from 'src/utils/general';

const key_name = 'session_id';

export const useDocLockStore = defineStore('doc_lock', {
  state: () => ({
    doc_lock: {
      document_version: '' as string,
      locked_by: '' as string,
      locked_on: '' as string,
      success: false as boolean,
      key: '' as string,
      error: {} as dynamicObj,
    },
    acquire_action_lock: false,
    refresh: true,
    store_disable: false,
  }),
  getters: {
    getData(state) {
      return state;
    },
  },
  actions: {
    isStoreDisable() {
      return this.store_disable;
    },

    setDocLockStoreDisable(disable = true) {
      this.store_disable = disable;
    },

    async acquireDocLock(
      force = false,
      id?: string,
      document_version?: string,
    ) {
      try {
        if (this.isStoreDisable()) {
          return;
        }

        const doc_store = useDocumentStore();
        const doc_id = !id ? doc_store.record.doc_id : id;

        if (!doc_id) {
          return;
        }
        const doc_version = document_version
          ? document_version
          : !doc_store.record.document_version
            ? 1
            : doc_store.record.document_version;
        const data = {
          doc_id: doc_id,
          document_version: String(doc_version),
          force: force.toString(),
          session_key: this.getUniqueLockId(),
        };
        const response = await documentLockApiService.acquire(data);
        if (
          !response ||
          (response.hasOwnProperty('success') && response.success === false)
        ) {
          throw response;
        }

        if (response.success === true) {
          this.doc_lock.success = true;
          this.doc_lock.document_version = response.data.document_version;
          this.doc_lock.key = response.data.lock_key;
          this.refresh === true;
        }
      } catch (err: any) {
        this.doc_lock.success = false;
        this.doc_lock.error = err;
      } finally {
      }
    },

    async releaseDocLock(data: releaseRequest) {
      try {
        if (
          this.isStoreDisable() ||
          isUndefinedOrNull(this.doc_lock) ||
          isUndefinedOrNull(this.doc_lock.key) ||
          this.doc_lock.key === '' ||
          !data.doc_id
        ) {
          return;
        }

        this.refresh = false;
        const optionsDefault = {
          lock_key: this.doc_lock.key,
          force: false.toString(),
        };

        const params = { ...optionsDefault, ...data };
        await documentLockApiService.release(params);
      } catch (err: any) {
        logError('error while releasing the Document Lock', err);
      }
    },

    timeout(ms: number) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async acquireLockForActionBtn() {
      if (this.isStoreDisable()) {
        return;
      }
      this.acquire_action_lock = true;
      await this.acquireDocLock();
    },

    async hasActionAcquiredLock() {
      try {
        if (this.isStoreDisable()) {
          return true;
        }
        await this.acquireLockForActionBtn();
        if (
          !(this.acquire_action_lock === true && this.doc_lock.success === true)
        ) {
          throw {};
        }
        this.acquire_action_lock = false;
        return true;
      } catch (err) {
        return false;
      }
    },

    getDocLockError(): string | null {
      const errorMessage = this.doc_lock.error;

      if (
        errorMessage &&
        typeof errorMessage === 'object' &&
        'message' in errorMessage
      ) {
        return String((errorMessage as { message: unknown }).message);
      }

      return null;
    },

    saveUniqueLockId() {
      LocalStorage.set(key_name, uid());
    },

    getUniqueLockId() {
      if (!LocalStorage.has(key_name)) {
        this.saveUniqueLockId();
      }
      let value: string | void = String(LocalStorage.getItem(key_name));
      value = value === 'null' ? this.saveUniqueLockId() : value;
      return String(value);
    },
  },
});
